import React, { useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { 
  MapPin, 
  Phone, 
  Mail, 
  MessageSquare,
  Send,
  Home,
  Award,
  Star,
  ExternalLink,
  Instagram,
  Facebook,
  Twitter
} from "lucide-react";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    projectType: "",
    message: ""
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Prepare email content
    const subject = encodeURIComponent(`Property Project Inquiry - ${formData.projectType || 'General'}`);
    const body = encodeURIComponent(`
Hello Sddolphinflipper Team,

I am interested in discussing a property project with you. Here are my details:

Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone || 'Not provided'}
Project Type: ${formData.projectType || 'Not specified'}

Project Details:
${formData.message}

Please contact me at your earliest convenience to discuss this project further.

Best regards,
${formData.name}
    `);
    
    // Open email client
    window.location.href = `mailto:automaticartinc@gmail.com?subject=${subject}&body=${body}`;
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Phone",
      details: ["+1 (424) 283-0606", "Call or text anytime"],
      color: "orange"
    },
    {
      icon: Mail,
      title: "Business Email", 
      details: ["automaticartinc@gmail.com", "Professional inquiries welcome"],
      color: "red"
    },
    {
      icon: MessageSquare,
      title: "WhatsApp",
      details: ["+1 (424) 283-0606", "Quick messaging available"],
      color: "green",
      isWhatsApp: true
    }
  ];

  const services = [
    {
      title: "Property Acquisition",
      description: "Expert property evaluation and strategic purchasing",
      icon: Home
    },
    {
      title: "Complete Renovation",
      description: "Full-scale remodeling with creative design solutions",
      icon: Award
    },
    {
      title: "Project Management", 
      description: "Professional oversight from start to finish",
      icon: Star
    }
  ];

  return (
    <div className="min-h-screen pt-16 bg-gradient-to-br from-amber-50 via-orange-50 to-red-50">
      {/* Header Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Get In <span className="text-amber-600">Touch</span>
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-orange-500 mx-auto mb-8"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ready to transform your property vision into reality? Let's discuss your next project 
              and explore how Sddolphinflipper can bring your dreams to life.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information Grid */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-20 max-w-4xl mx-auto">
            {contactInfo.map((item, index) => {
              const IconComponent = item.icon;
              const colorClasses = {
                amber: "from-amber-50 to-orange-50 border-amber-200 bg-amber-500",
                orange: "from-orange-50 to-red-50 border-orange-200 bg-orange-500",
                red: "from-red-50 to-amber-50 border-red-200 bg-red-500",
                green: "from-green-50 to-emerald-50 border-green-200 bg-green-500"
              };
              
              const handleClick = () => {
                if (item.isWhatsApp) {
                  const phoneNumber = item.details[0].replace(/\D/g, '');
                  const message = encodeURIComponent("Hi, I'm interested in discussing a property project with Sddolphinflipper.");
                  window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
                } else if (item.icon === Phone) {
                  window.open(`tel:${item.details[0]}`, '_self');
                } else if (item.icon === Mail) {
                  window.open(`mailto:${item.details[0]}?subject=Property Project Inquiry`, '_self');
                }
              };
              
              return (
                <Card 
                  key={index} 
                  className={`bg-gradient-to-br ${colorClasses[item.color]} shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 ${item.isWhatsApp || item.icon === Phone || item.icon === Mail ? 'cursor-pointer' : ''}`}
                  onClick={handleClick}
                >
                  <CardContent className="p-4 sm:p-6 text-center">
                    <div className={`${colorClasses[item.color].split(' ').pop()} p-3 sm:p-4 rounded-full w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 flex items-center justify-center`}>
                      <IconComponent className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">{item.title}</h3>
                    {item.details.map((detail, detailIndex) => (
                      <p key={detailIndex} className={`text-gray-600 text-sm sm:text-base ${detailIndex === 0 ? 'font-semibold' : 'text-xs sm:text-sm'}`}>
                        {detail}
                      </p>
                    ))}
                    {item.isWhatsApp && (
                      <div className="mt-2">
                        <span className="inline-flex items-center text-xs text-green-600">
                          <ExternalLink className="w-3 h-3 mr-1" />
                          Click to chat
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto">
            {/* Contact Form */}
            <Card className="bg-white/90 backdrop-blur-sm border-amber-200 shadow-xl">
              <CardContent className="p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row sm:items-center mb-6">
                  <div className="bg-amber-500 p-3 rounded-full mr-0 sm:mr-4 mb-3 sm:mb-0 w-fit">
                    <MessageSquare className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Start Your Project</h2>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="border-amber-200 focus:border-amber-500 text-sm sm:text-base"
                        placeholder="Your name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="border-amber-200 focus:border-amber-500 text-sm sm:text-base"
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="border-amber-200 focus:border-amber-500 text-sm sm:text-base"
                        placeholder="(424) 283-0606"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Project Type
                      </label>
                      <select
                        name="projectType"
                        value={formData.projectType}
                        onChange={handleInputChange}
                        className="w-full p-2 sm:p-3 border border-amber-200 rounded-md focus:border-amber-500 focus:outline-none text-sm sm:text-base"
                      >
                        <option value="">Select project type</option>
                        <option value="full-renovation">Complete Renovation</option>
                        <option value="kitchen">Kitchen Remodel</option>
                        <option value="bathroom">Bathroom Renovation</option>
                        <option value="investment">Investment Property</option>
                        <option value="consultation">Consultation</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project Details *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      className="border-amber-200 focus:border-amber-500 text-sm sm:text-base"
                      placeholder="Tell us about your project vision, timeline, and any specific requirements..."
                      required
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-3 sm:py-4 text-base sm:text-lg font-medium rounded-lg shadow-lg hover:scale-105 transition-all duration-300"
                  >
                    <Send className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    Send Message
                  </Button>
                  
                  {/* Quick Contact Buttons */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4 border-t border-gray-200">
                    <Button
                      type="button"
                      variant="outline"
                      className="border-green-200 text-green-700 hover:bg-green-50 py-2 sm:py-3"
                      onClick={() => {
                        const phoneNumber = "+14242830606";
                        const message = encodeURIComponent("Hi, I'm interested in discussing a property project with Sddolphinflipper.");
                        window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
                      }}
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="border-blue-200 text-blue-700 hover:bg-blue-50 py-2 sm:py-3"
                      onClick={() => window.open('tel:+14242830606', '_self')}
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Call Now
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Services & Info */}
            <div className="space-y-6 sm:space-y-8">
              {/* Services Overview */}
              <Card className="bg-white/90 backdrop-blur-sm border-orange-200 shadow-xl">
                <CardContent className="p-6 sm:p-8">
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Our Services</h3>
                  <div className="space-y-4 sm:space-y-6">
                    {services.map((service, index) => {
                      const IconComponent = service.icon;
                      return (
                        <div key={index} className="flex items-start space-x-3 sm:space-x-4">
                          <div className="bg-orange-500 p-2 sm:p-3 rounded-full flex-shrink-0">
                            <IconComponent className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 mb-1 text-sm sm:text-base">{service.title}</h4>
                            <p className="text-gray-600 text-xs sm:text-sm">{service.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Why Choose Us */}
              <Card className="bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-xl">
                <CardContent className="p-6 sm:p-8">
                  <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Why Choose Sddolphinflipper?</h3>
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center space-x-3">
                      <Star className="w-4 h-4 sm:w-5 sm:h-5 text-amber-200 flex-shrink-0" />
                      <span className="text-sm sm:text-base">11+ years of San Diego market expertise</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Star className="w-4 h-4 sm:w-5 sm:h-5 text-amber-200 flex-shrink-0" />
                      <span className="text-sm sm:text-base">Multiple successfully completed property renovations</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Star className="w-4 h-4 sm:w-5 sm:h-5 text-amber-200 flex-shrink-0" />
                      <span className="text-sm sm:text-base">50+ local craftsmen employed</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Star className="w-4 h-4 sm:w-5 sm:h-5 text-amber-200 flex-shrink-0" />
                      <span className="text-sm sm:text-base">Creative designs with innovative textures</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Star className="w-4 h-4 sm:w-5 sm:h-5 text-amber-200 flex-shrink-0" />
                      <span className="text-sm sm:text-base">Community-focused development approach</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Emergency Contact */}
              <Card className="bg-green-50 border-green-200 shadow-lg">
                <CardContent className="p-4 sm:p-6 text-center">
                  <h4 className="text-base sm:text-lg font-bold text-green-800 mb-2">Need Quick Response?</h4>
                  <p className="text-green-600 mb-4 text-sm sm:text-base">WhatsApp for instant communication</p>
                  <Button 
                    size="sm"
                    className="bg-green-500 hover:bg-green-600 text-white text-sm sm:text-base px-4 py-2"
                    onClick={() => {
                      const phoneNumber = "+14242830606";
                      const message = encodeURIComponent("Hi, I need urgent assistance with a property project.");
                      window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
                    }}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    WhatsApp: +1 (424) 283-0606
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
